import React, { useMemo, useState } from "react";

import { Loader2, Search } from "lucide-react";

import { FLOW, pubLabel } from "./flowCopy";
import {
  BUCKETS,
  BUCKET_LABEL,
  BUCKET_STYLE,
  LastActivity,
  NO_PLAN_BUTTON,
  PlanBucket,
  PlanRowState,
  RowAction,
  RowActionKind,
  bucketRank,
  lastActivityFromServer,
  lastActivityFromStatus,
  planRowState,
} from "./planBucket";
import { shortDate } from "./shared";
import { RenewalCell, formatMoney } from "./SponsorPlansList";
import type { PortfolioAccountRow } from "../../Store/Reducers/MyPortfolioOverviewSlice";
import type { AccountPlanStatus, Persona } from "../../Types/AccountPlan";

/** "just now", "3 hours ago", "yesterday", "12 days ago", "3 months ago". */
export const timeAgo = (iso?: string | null, now = Date.now()): string => {
  if (!iso) return "";
  const mins = Math.floor((now - new Date(iso).getTime()) / 60000);
  if (mins < 60) return "just now";
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "yesterday";
  if (days < 45) return `${days} days ago`;
  const months = Math.round(days / 30);
  return `${months} month${months === 1 ? "" : "s"} ago`;
};

export const StatusPill: React.FC<{ bucket: PlanBucket }> = ({ bucket }) => (
  <span
    className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold ${BUCKET_STYLE[bucket].pill}`}
  >
    <span
      className={`h-1.5 w-1.5 rounded-full ${BUCKET_STYLE[bucket].dot}`}
      aria-hidden
    />
    {BUCKET_LABEL[bucket]}
  </span>
);

interface Props {
  accounts: PortfolioAccountRow[];
  statuses: Record<string, AccountPlanStatus>;
  /** The roles the viewer acts in on an account (AE and/or CSM). */
  rolesOn: (id: string) => Persona[];
  portfolioLoading: boolean;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  orgWide: boolean;
  /** The admin's "Viewing as" switch, when there is one. */
  personaSwitch: React.ReactNode;
  /** The row opens the account panel. */
  openRow: (id: string) => void;
  onAction: (id: string, kind: RowActionKind) => void;
  /** The Quick look: "Open published plan" for someone not on the account. */
  /** Unused since board 01 (2026-10-31): a published briefing opens for everyone. */
  openQuickLook?: (id: string) => void;
  /** Only the account's AE (or an admin) uploads. */
  canUpload: (id: string) => boolean;
  /** Whether the viewer may open the plan page (someone on the account, or an admin). */
  canOpenPlan?: (id: string) => boolean;
  /** From Activity: the newest thing a person did on each account's plans. */
  lastActivity?: Record<string, LastActivity>;
  /** My accounts / All accounts; null hides the switch (Exec Ops, sponsors). */
  scope?: "mine" | "all" | null;
  onScope?: (scope: "mine" | "all") => void;
  parseCalendarDate: (v: string) => Date;
}

/**
 * The account team's Account Plans list (redesign, 2026-10-29): one status per
 * account, a status bar that adds up to the portfolio and filters the table, and
 * one button per row for the next step.
 */
const TeamPlansList: React.FC<Props> = ({
  accounts,
  statuses,
  rolesOn,
  portfolioLoading,
  loading,
  error,
  refresh,
  orgWide,
  personaSwitch,
  openRow,
  onAction,
  canUpload,
  canOpenPlan = () => true,
  lastActivity = {},
  scope = null,
  onScope,
  parseCalendarDate,
}) => {
  const [filter, setFilter] = useState<PlanBucket | null>(null);
  const [query, setQuery] = useState("");

  const states = useMemo(() => {
    const out: Record<string, PlanRowState> = {};
    accounts.forEach(a => {
      const s = statuses[a.customer_id];
      if (s) out[a.customer_id] = planRowState(s, rolesOn(a.customer_id));
    });
    return out;
  }, [accounts, statuses, rolesOn]);

  const counts = useMemo(() => {
    const c: Record<PlanBucket, number> = {
      action: 0,
      progress: 0,
      review: 0,
      published: 0,
      none: 0,
    };
    Object.keys(states).forEach(id => {
      c[states[id].bucket] += 1;
    });
    return c;
  }, [states]);
  const known = Object.keys(states).length;

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rank = (id: string) =>
      states[id] ? bucketRank(states[id].bucket) : BUCKETS.length;
    return accounts
      .filter(
        a =>
          (!q || a.customer_name.toLowerCase().includes(q)) &&
          (!filter || states[a.customer_id]?.bucket === filter)
      )
      .sort(
        (a, b) =>
          rank(a.customer_id) - rank(b.customer_id) ||
          a.customer_name.localeCompare(b.customer_name)
      );
  }, [accounts, states, query, filter]);

  const pending = portfolioLoading || (loading && !known);
  const total = accounts.length;
  const summary = filter
    ? `${BUCKET_LABEL[filter]} · ${counts[filter]} of ${total}`
    : `${total} account${total === 1 ? "" : "s"} · action needed first`;

  return (
    <div className="space-y-5">
      {personaSwitch && <div className="flex justify-end">{personaSwitch}</div>}

      <section
        aria-label="Status of all accounts"
        className="space-y-4 rounded-xl border border-gray-200 bg-white px-5 py-5"
      >
        <div
          className="flex h-2.5 gap-0.5 overflow-hidden rounded-full bg-gray-100"
          aria-hidden
        >
          {!pending &&
            BUCKETS.filter(b => counts[b] > 0).map(b => (
              <div
                key={b}
                style={{ flexGrow: counts[b], flexBasis: 0 }}
                className={`${BUCKET_STYLE[b].bar} ${filter && filter !== b ? "opacity-25" : ""}`}
              />
            ))}
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-5">
          {BUCKETS.map(b => {
            const on = filter === b;
            return (
              <button
                key={b}
                type="button"
                aria-pressed={on}
                onClick={() => setFilter(on ? null : b)}
                className={`flex min-h-[44px] flex-col items-start gap-1 rounded-lg border px-3.5 py-3 text-left transition focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${on ? "border-[#9333EA] bg-purple-50 ring-1 ring-[#9333EA]" : "border-gray-200 bg-white hover:border-purple-300"}`}
              >
                <span className="flex items-center gap-2 text-[13px] font-medium text-gray-600">
                  <span
                    className={`h-2 w-2 rounded-full ${BUCKET_STYLE[b].dot}`}
                    aria-hidden
                  />
                  {BUCKET_LABEL[b]}
                </span>
                <span
                  className="text-2xl font-semibold tracking-tight text-gray-900"
                  data-testid={`bucket-count-${b}`}
                >
                  {pending ? "…" : counts[b]}
                </span>
              </button>
            );
          })}
        </div>
      </section>

      {error && (
        <p
          role="alert"
          className="flex items-center gap-2 text-sm text-red-600"
        >
          {error}
          <button
            type="button"
            onClick={refresh}
            className="font-medium text-red-700 underline underline-offset-2"
          >
            Try again
          </button>
        </p>
      )}

      <section
        aria-label="Accounts"
        className="overflow-hidden rounded-xl border border-gray-200 bg-white"
      >
        <div className="flex flex-wrap items-center gap-3 border-b border-gray-100 px-5 py-3.5">
          <label className="flex h-10 min-w-[220px] max-w-sm flex-1 items-center gap-2 rounded-lg border border-gray-200 px-3 text-gray-400 focus-within:border-purple-400">
            <Search className="h-4 w-4" aria-hidden />
            <input
              type="search"
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search accounts"
              aria-label="Search accounts"
              className="min-w-0 flex-1 border-0 bg-transparent text-sm text-gray-900 outline-none"
            />
          </label>
          <span className="text-[13px] text-gray-500">{summary}</span>
          {scope && onScope && (
            <div
              role="group"
              aria-label="Whose accounts"
              className="ml-auto inline-flex rounded-lg border border-gray-200 bg-gray-50 p-0.5"
            >
              {(
                [
                  ["mine", "My accounts"],
                  ["all", "All accounts"],
                ] as const
              ).map(([v, label]) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={scope === v}
                  onClick={() => onScope(v)}
                  className={`rounded-md px-3 py-1.5 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 ${scope === v ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}
          {filter && (
            <button
              type="button"
              onClick={() => setFilter(null)}
              className="rounded px-2 py-2 text-[13px] font-medium text-[#7E22CE] hover:underline"
            >
              Clear filter
            </button>
          )}
        </div>

        {/* LIST-06, F-UF-E12: fits at 1280 px with no sideways scroll. The two
            text columns truncate (full text in `title`); renewal stacks its chip. */}
        <div className="overflow-x-auto">
          <table className="w-full table-fixed text-sm">
            <thead className="text-left align-bottom text-xs text-gray-500">
              <tr>
                <th className="w-[21%] px-5 py-3 font-medium">Account</th>
                <th className="w-[25%] px-4 py-3 font-medium">{FLOW.status}</th>
                <th className="w-[15%] px-4 py-3 font-medium">
                  {FLOW.lastPublished}
                  <span className="block text-[11px] font-normal text-gray-400">
                    what the sponsor sees
                  </span>
                </th>
                <th className="w-[15%] px-4 py-3 font-medium">
                  {FLOW.lastActivity}
                </th>
                <th className="w-24 px-4 py-3 font-medium">Renewal</th>
                {/* Wide enough for "View progress" (board 01 labels) at 1280 px. */}
                <th className="w-36 px-3 py-3">
                  <span className="sr-only">Next step</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr className="border-t border-gray-100">
                  <td
                    colSpan={6}
                    className="px-5 py-8 text-center text-sm text-gray-500"
                  >
                    {portfolioLoading ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2
                          className="h-4 w-4 animate-spin text-[#974ae4]"
                          aria-hidden
                        />
                        {orgWide
                          ? "Loading accounts…"
                          : "Loading your accounts…"}
                      </span>
                    ) : accounts.length ? (
                      "No accounts match."
                    ) : orgWide ? (
                      "No accounts in this organization yet."
                    ) : (
                      "No accounts in your portfolio yet."
                    )}
                  </td>
                </tr>
              ) : (
                rows.map(a => {
                  const id = a.customer_id;
                  const s = statuses[id];
                  const row = states[id];
                  // Activity knows every step a person took (a verify, a publish);
                  // the status only the upload and the publish. Whichever is newer.
                  // Phase 4: the server's last_activity wins when it is sent.
                  const fromServer = lastActivityFromServer(s);
                  const fromStatus = lastActivityFromStatus(s);
                  const fromActivity = lastActivity[id];
                  const last = fromServer
                    ? fromServer
                    : fromActivity &&
                        (!fromStatus ||
                          new Date(fromActivity.at).getTime() >=
                            new Date(fromStatus.at).getTime())
                      ? fromActivity
                      : fromStatus;
                  // Someone in the organization who isn't on the account reads the
                  // published briefing only (board 01, 2026-10-31): "View plan" opens
                  // it once there is one, and is greyed out until then.
                  const member = canOpenPlan(id);
                  const teamButton =
                    row?.button?.kind === "upload" && !canUpload(id)
                      ? null
                      : row?.button || null;
                  const button: RowAction | null = member
                    ? teamButton
                    : s?.published
                      ? {
                          label: FLOW.viewPlan,
                          kind: "briefing",
                          primary: false,
                        }
                      : NO_PLAN_BUTTON;
                  const pressButton = () => {
                    if (!button || button.disabled) return;
                    onAction(id, button.kind);
                  };
                  const openPublished = () => onAction(id, "briefing");
                  const sub = [
                    a.revenue != null ? `${formatMoney(a.revenue)} ARR` : null,
                    a.industry,
                  ]
                    .filter(Boolean)
                    .join(" · ");
                  return (
                    <tr
                      key={id}
                      onClick={() => openRow(id)}
                      onKeyDown={e =>
                        e.key === "Enter" &&
                        e.target === e.currentTarget &&
                        openRow(id)
                      }
                      tabIndex={0}
                      data-bucket={row?.bucket}
                      className="cursor-pointer border-t border-gray-100 transition hover:bg-purple-50/40 focus:bg-purple-50/40 focus:outline-none"
                    >
                      <td className="px-5 py-3.5">
                        <div
                          className="truncate font-semibold text-gray-900"
                          title={a.customer_name}
                        >
                          {a.customer_name}
                        </div>
                        {sub && (
                          <div className="mt-0.5 truncate text-xs text-gray-500">
                            {sub}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3.5">
                        {row ? (
                          <div className="flex flex-col items-start gap-1">
                            <StatusPill bucket={row.bucket} />
                            <span
                              className={`max-w-full truncate text-xs ${row.problem && row.bucket === "action" ? "font-medium text-red-700" : row.overdue ? "font-medium text-amber-700" : "text-gray-500"}`}
                              title={row.detail}
                              data-testid="row-detail"
                              data-problem={row.problem ? "true" : undefined}
                              data-overdue={row.overdue ? "true" : undefined}
                            >
                              {row.detail}
                            </span>
                          </div>
                        ) : loading ? (
                          <Loader2
                            className="h-3.5 w-3.5 animate-spin text-gray-300"
                            aria-hidden
                          />
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td
                        className="px-4 py-3.5 text-gray-700"
                        data-testid="last-published"
                      >
                        {s?.published ? (
                          <>
                            <div
                              className="truncate"
                              title={`${pubLabel(s.published)} · ${shortDate(s.published.at)}`}
                            >
                              <span className="font-semibold">
                                {pubLabel(s.published)}
                              </span>{" "}
                              · {shortDate(s.published.at)}
                            </div>
                            <button
                              type="button"
                              onClick={e => {
                                e.stopPropagation();
                                openPublished();
                              }}
                              className="mt-0.5 block max-w-full truncate rounded text-left text-xs font-medium text-[#7E22CE] hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
                              aria-label={`${FLOW.openPublished} for ${a.customer_name}`}
                            >
                              {FLOW.openPublished}
                            </button>
                          </>
                        ) : s ? (
                          <span className="text-gray-400">Not yet</span>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td
                        className="px-4 py-3.5 text-gray-700"
                        data-testid="last-activity"
                      >
                        {last ? (
                          <>
                            <div className="truncate" title={last.what}>
                              {last.what}
                            </div>
                            <div
                              className="truncate text-xs text-gray-500"
                              title={[last.by, timeAgo(last.at)]
                                .filter(Boolean)
                                .join(" · ")}
                            >
                              {[last.by, timeAgo(last.at)]
                                .filter(Boolean)
                                .join(" · ")}
                            </div>
                          </>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3.5 text-sm">
                        <RenewalCell
                          date={a.renewal_date}
                          risk={s?.renewal_risk}
                          parse={parseCalendarDate}
                          stacked
                        />
                      </td>
                      <td className="px-3 py-3.5 text-right">
                        {button && (
                          <button
                            type="button"
                            data-testid="row-action"
                            data-kind={button.kind}
                            disabled={button.disabled}
                            title={button.why}
                            onClick={e => {
                              e.stopPropagation();
                              pressButton();
                            }}
                            aria-label={`${button.label}: ${a.customer_name}`}
                            className={`inline-flex h-9 items-center whitespace-nowrap rounded-lg px-3 text-sm font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:cursor-not-allowed disabled:border-gray-100 disabled:bg-gray-50 disabled:text-gray-400 ${button.primary ? "bg-[#9333EA] text-white hover:bg-[#7E22CE]" : "border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"}`}
                          >
                            {button.label}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default TeamPlansList;
